import { t } from 'i18next'
import React, { FunctionComponent } from 'react'
import { useHistory } from 'react-router'

import { getProgramInfoVariantConfig } from './UnenrolledProgramsInfo.config'
import { StyledUnenrolledProgramsInfoWrapper } from './UnenrolledProgramsInfo.styles'
import { ClinTheme } from '../../../../ClinTheme'
import { ClinButton } from '../../../../components/ClinButton'
import { ClinIcon } from '../../../../components/ClinIcon'
import { ClinIconPathName } from '../../../../components/ClinIcon/ClinIcon.paths'
import { ClinText } from '../../../../components/ClinText'
import { AnalyticsEvent } from '../../../../services/Analytics'
import analyticsServiceSingleton from '../../../../services/Analytics/initAnalytics'
import { CountryDto } from '../../../../types/swaggerTypes'
import {
  isPostTrialAccessOnlyProgramStatus,
  PTA_ENROLMENT_RESTRICTED_MESSAGE
} from '../../../Patients/patientInitialsDisplay'
import { SideBarMode } from '../ProgramDetail.models'
import { StyledContact, StyledContactLink } from '../ProgramDetail.styles'

interface IUnenrolledProgramsInfoProps {
  programInfoVariant: SideBarMode | undefined
  programId?: number
  /** Program-level status. Used to detect PTA restriction (mirrors ProgramDetailHeader logic). */
  programStatus?: string
  /** Country-level program status from the programme catalogue. Used to detect PTA restriction. */
  programCountryStatus?: string
  /** Support contact details */
  supportContact?: CountryDto
}

export const UnenrolledProgramsInfo: FunctionComponent<
  IUnenrolledProgramsInfoProps
> = ({
  programInfoVariant,
  programId,
  programStatus,
  programCountryStatus,
  supportContact
}) => {
  const history = useHistory()
  const variantConfig = getProgramInfoVariantConfig(programInfoVariant)
  // CLOS-13391: PTA programmes restrict self-enrolment; hide the Enrol button and show
  // the restricted message instead of the normal not-enrolled description.
  // Mirrors ProgramDetailHeader: block on either program-level OR country-level PTA status.
  const isPtaRestricted =
    programInfoVariant === SideBarMode.NotEnrolled &&
    (isPostTrialAccessOnlyProgramStatus(programStatus) ||
      isPostTrialAccessOnlyProgramStatus(programCountryStatus))

  const handleButtonClick = () => {
    if (programInfoVariant === SideBarMode.NotEnrolled) {
      programId &&
        history.push(`/programs/access-programs/${programId}/enrolment-form`)
    } else if (programInfoVariant === SideBarMode.NotAvailableInYourCountry) {
      programId &&
        history.push(`/programs/access-programs/${programId}/register-interest`)
    }
  }
  return (
    variantConfig && (
      <StyledUnenrolledProgramsInfoWrapper
        paddintTop={variantConfig.paddingTop}
        paddingBottom={variantConfig.paddingBottom}
      >
        <ClinText
          fontSize={ClinTheme.fontSizes[2]}
          width={programInfoVariant === SideBarMode.NotEnrolled ? '60%' : '50%'}
          textAlign="center"
          marginBottom={
            programInfoVariant === SideBarMode.Closed
              ? ClinTheme.space[5]
              : undefined
          }
        >
          {isPtaRestricted
            ? PTA_ENROLMENT_RESTRICTED_MESSAGE
            : t(variantConfig.description)}
        </ClinText>
        {!isPtaRestricted &&
          variantConfig.showButton &&
          variantConfig.buttonText && (
            <ClinButton
              variant="purpleSecondaryNarrow"
              style={{ width: variantConfig.buttonWidth, marginTop: '16px' }}
              onClick={() => handleButtonClick()}
            >
              {t(variantConfig.buttonText)}
            </ClinButton>
          )}
        {programInfoVariant === SideBarMode.Closed && supportContact && (
          <>
            {supportContact.csPhoneNumber && (
              <StyledContact>
                <ClinIcon iconName={ClinIconPathName.Phone} />
                <StyledContactLink
                  href={`tel:${supportContact.rawPhoneNumber}`}
                >
                  <ClinText color={ClinTheme.colors.primaryMid} as="span">
                    {supportContact.csPhoneNumber}
                  </ClinText>
                </StyledContactLink>
              </StyledContact>
            )}
            {supportContact.csEmailAddress && (
              <StyledContact>
                <ClinIcon iconName={ClinIconPathName.Mail} />
                <StyledContactLink
                  href={`mailto:${supportContact.csEmailAddress}`}
                  onClick={() => {
                    analyticsServiceSingleton.trackEvent(
                      AnalyticsEvent.ContactClicked,
                      { contactType: 'email' }
                    )
                  }}
                >
                  <ClinText
                    color={ClinTheme.colors.primaryMid}
                    as="span"
                    wordBreak="break-all"
                  >
                    {supportContact.csEmailAddress}
                  </ClinText>
                </StyledContactLink>
              </StyledContact>
            )}
          </>
        )}
        {variantConfig.phoneLink &&
          programInfoVariant !== SideBarMode.Closed && (
            <ClinText
              marginTop="12px"
              as="span"
              color={ClinTheme.colors.tealishBlue}
              lineHeight={ClinTheme.lineHeights.heading[0]}
            >
              {variantConfig.phoneLink}
            </ClinText>
          )}
        {variantConfig.emailLink &&
          programInfoVariant !== SideBarMode.Closed && (
            <a
              className="raw-email"
              href={`mailto:${variantConfig.emailLink}`}
              style={{ overflowWrap: 'break-word' }}
            >
              <ClinText
                className="email"
                as="span"
                color={ClinTheme.colors.tealishBlue}
                lineHeight={ClinTheme.lineHeights.heading[0]}
              >
                {variantConfig.emailLink}
              </ClinText>
            </a>
          )}
      </StyledUnenrolledProgramsInfoWrapper>
    )
  )
}
